import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../theme/ThemeProvider.js';
import { ThemeStore } from '../../theme/ThemeStore.js';
import { getClassroomShowMascot, setClassroomShowMascot } from '../../theme/themeStorage.js';
import { PageStore } from '../../classroomPages/PageStore.js';
import { getPageCatalogEntry } from '@classy/shared';
import { CLASSROOM_PAGE_MODULES, type ClassroomPageKey } from '../../classroomPages/registry.js';
import { useApi } from '../../api/ApiProvider.js';
import { useClassroomSettings } from '../../hooks/useClassroomSettings.js';
import type { ClassroomSettingsDto } from '../../api/types.js';
import styles from './GeneralTab.module.css';

const GRADE_RANGES: { value: 'k3' | 'g48'; label: string; note: string }[] = [
  { value: 'k3', label: 'K–3', note: 'Greeting + message, mascot front and center.' },
  {
    value: 'g48',
    label: '4–8',
    note: 'Adds daily To-Do and Turn-In boards; hides the mascot by default (toggle it back on above).',
  },
];

const PAGE_GLYPHS: Record<string, string> = {
  home: '🏠',
  calendar: '📅',
  'day-of-school': '🔢',
  weather: '☀️',
  feelings: '💛',
  question: '❓',
  pledges: '🙋',
  number: '✏️',
  youtube: '▶',
};

const PAGE_BLURBS: Record<string, string> = {
  home: 'Greeting and message-of-the-day card.',
  calendar: 'Monthly grid with events + birthdays.',
  'day-of-school': 'Hundreds-chart day counter.',
  weather: 'Live conditions for the classroom.',
  feelings: 'Daily feelings check-in.',
  question: 'Question of the day with answers.',
  pledges: 'Classroom pledges said each morning.',
  number: 'Place value, currency, and number neighbors.',
  youtube: 'A lesson playlist of embedded YouTube videos.',
};

export interface GeneralTabProps {
  classroomId: string;
  enabledPages: readonly ClassroomPageKey[];
  onAddPage: (key: ClassroomPageKey) => void;
  onRemovePage: (key: ClassroomPageKey) => void;
  onMovePage: (key: ClassroomPageKey, direction: 'left' | 'right') => void;
}

/**
 * General tab body. Shows the current theme as a card, the enabled
 * pages as a grid of richer cards (each with reorder + remove
 * controls), and "Browse" buttons that open the Theme Store / Page
 * Store modals. The Page Store only lists not-yet-added pages —
 * removing happens here on the General tab where the cards live.
 */
export function GeneralTab({
  classroomId,
  enabledPages,
  onAddPage,
  onRemovePage,
  onMovePage,
}: GeneralTabProps) {
  const theme = useTheme();
  const api = useApi();
  const qc = useQueryClient();
  const [themeStoreOpen, setThemeStoreOpen] = useState(false);
  const [pageStoreOpen, setPageStoreOpen] = useState(false);

  // Grade range decides which live Home layout this classroom shows. Stored
  // server-side (home_variant) so it follows the room across devices; the
  // shared settings query means Home + LiveSurface flip instantly on save.
  const settings = useClassroomSettings(classroomId);
  const homeVariant: 'k3' | 'g48' = settings.data?.homeVariant ?? 'k3';
  // Surfaced if a save fails — otherwise a failed PATCH looks identical to a
  // working one (the highlight just never moves), which reads as "it won't
  // let me select."
  const [gradeError, setGradeError] = useState<string | null>(null);
  const settingsKey = ['classroom-settings', classroomId] as const;
  const setGradeRange = useMutation({
    mutationFn: (next: 'k3' | 'g48') => api.home.patchSettings(classroomId, { homeVariant: next }),
    // Optimistically flip the highlight (and the shared cache, so the live
    // Home + mascot update too) the instant the teacher taps — no waiting on
    // the round-trip. Roll back and show an error if the save fails.
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: settingsKey });
      const prev = qc.getQueryData<ClassroomSettingsDto>(settingsKey);
      if (prev) qc.setQueryData<ClassroomSettingsDto>(settingsKey, { ...prev, homeVariant: next });
      setGradeError(null);
      return { prev };
    },
    onError: (err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData<ClassroomSettingsDto>(settingsKey, ctx.prev);
      setGradeError(err instanceof Error ? err.message : "Couldn't save — please try again.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: settingsKey }),
  });

  // Per-classroom "show mascot" toggle. Re-read on classroom change so
  // switching between rooms picks up the right state. The 4–8 home defaults
  // the mascot off (unless the teacher explicitly set it), so the toggle's
  // default tracks the grade range — but an explicit choice always wins.
  const [showMascot, setShowMascot] = useState(() =>
    getClassroomShowMascot(classroomId, homeVariant !== 'g48'),
  );
  useEffect(() => {
    setShowMascot(getClassroomShowMascot(classroomId, homeVariant !== 'g48'));
  }, [classroomId, homeVariant]);
  function toggleShowMascot() {
    const next = !showMascot;
    setShowMascot(next);
    setClassroomShowMascot(classroomId, next);
  }

  const currentPack = theme.registry.get(theme.manifest.key);
  const currentBg = currentPack?.getBackground('home');
  const currentMascot = currentPack?.getMascot('home');

  // The "Pages" add/remove/reorder flow here is Morning-Meeting-only. Home and
  // the standalone class-level pages (YouTube, Links) are permanent — managed by
  // the Classroom Pages strip's enable/disable toggles, not added/removed here.
  const isMorningMeeting = (key: string) =>
    getPageCatalogEntry(key)?.placement === 'morning-meeting';
  const enabledModules = enabledPages
    .map((key) => CLASSROOM_PAGE_MODULES.find((m) => m.key === key))
    .filter((m): m is (typeof CLASSROOM_PAGE_MODULES)[number] => !!m && isMorningMeeting(m.key));

  const totalAvailable = CLASSROOM_PAGE_MODULES.filter((m) => isMorningMeeting(m.key)).length;
  const allAdded = enabledModules.length >= totalAvailable;

  // Removing a page is reversible by default — settings and data stay on
  // the server and reappear the moment the teacher re-adds the page.
  // Permanent wipe is the explicit "Reset settings" button on each
  // page's own settings panel. So no confirm dialog here.

  return (
    <div className={styles.summary}>
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Theme</h2>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => setThemeStoreOpen(true)}
            data-testid="open-theme-store"
          >
            Browse themes
          </button>
        </div>
        <button
          type="button"
          className={styles.themeRow}
          onClick={() => setThemeStoreOpen(true)}
          data-testid="open-theme-store-card"
          aria-label={`Change theme. Currently ${theme.manifest.name}.`}
        >
          <div
            className={styles.themePreview}
            style={currentBg ? { backgroundImage: `url(${currentBg})` } : undefined}
            aria-hidden="true"
          >
            {currentMascot ? (
              <img className={styles.themePreviewMascot} src={currentMascot} alt="" />
            ) : null}
          </div>
          <div className={styles.themeMeta}>
            <span className={styles.themeName}>{theme.manifest.name}</span>
            <span className={styles.themeNote}>
              Sets the background scenes and the home-page mascot for this classroom.
            </span>
          </div>
        </button>

        {/* Per-classroom toggle: older grades / cleaner-look rooms can
         *  hide the theme mascot. Default ON. */}
        <label className={styles.toggleRow} data-testid="show-mascot-toggle">
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Show mascot</span>
            <span className={styles.toggleNote}>
              Display the theme&apos;s mascot on the live screens. Turn off for older grades or a
              less-cartoony look.
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={showMascot}
            onChange={toggleShowMascot}
            className={styles.toggleSwitch}
            aria-checked={showMascot}
          />
        </label>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Grade range</h2>
        </div>
        <p className={styles.sectionHint}>
          Picks the live Home screen. 4–8 swaps the mascot for daily To-Do and Turn-In boards you
          plan a week ahead.
        </p>
        <div
          className={styles.segmented}
          role="radiogroup"
          aria-label="Grade range"
          data-testid="grade-range"
        >
          {GRADE_RANGES.map((g) => {
            const selected = homeVariant === g.value;
            return (
              <button
                key={g.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`${styles.segment} ${selected ? styles.segmentActive : ''}`}
                onClick={() => {
                  if (!selected) setGradeRange.mutate(g.value);
                }}
                disabled={settings.isLoading}
                data-testid={`grade-range-${g.value}`}
              >
                <span className={styles.segmentLabel}>{g.label}</span>
                <span className={styles.segmentNote}>{g.note}</span>
              </button>
            );
          })}
        </div>
        {gradeError ? (
          <p className={styles.gradeError} role="alert" data-testid="grade-range-error">
            Couldn&rsquo;t save the grade range: {gradeError}
          </p>
        ) : null}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Pages</h2>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => setPageStoreOpen(true)}
            disabled={allAdded}
            data-testid="open-page-store"
            title={allAdded ? 'All available pages are already added.' : undefined}
          >
            {allAdded ? 'All pages added' : '+ Add page'}
          </button>
        </div>
        <p className={styles.sectionHint}>
          Reorder with <span aria-label="left arrow">‹</span> /{' '}
          <span aria-label="right arrow">›</span>; remove with ✕. Order here drives the live
          morning-meeting tab strip.
        </p>

        {enabledModules.length === 0 ? (
          <p className={styles.empty}>
            No pages added yet — open the page store to pick what shows up in morning meeting.
          </p>
        ) : (
          <div className={styles.pageGrid}>
            {enabledModules.map((m, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === enabledModules.length - 1;
              return (
                <div key={m.key} className={styles.pageCard}>
                  <div className={styles.pageCardHead}>
                    <span className={styles.pageGlyph} aria-hidden="true">
                      {PAGE_GLYPHS[m.key] ?? '✦'}
                    </span>
                    <span className={styles.pageLabel}>{m.label}</span>
                  </div>
                  <p className={styles.pageBlurb}>{PAGE_BLURBS[m.key] ?? ''}</p>
                  <div className={styles.pageControls}>
                    <button
                      type="button"
                      className={styles.controlButton}
                      onClick={() => onMovePage(m.key as ClassroomPageKey, 'left')}
                      disabled={isFirst}
                      aria-label={`Move ${m.label} left`}
                      data-testid={`move-page-left-${m.key}`}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className={styles.controlButton}
                      onClick={() => onMovePage(m.key as ClassroomPageKey, 'right')}
                      disabled={isLast}
                      aria-label={`Move ${m.label} right`}
                      data-testid={`move-page-right-${m.key}`}
                    >
                      ›
                    </button>
                    <button
                      type="button"
                      className={`${styles.controlButton} ${styles.removeButton}`}
                      onClick={() => onRemovePage(m.key as ClassroomPageKey)}
                      aria-label={`Remove ${m.label}`}
                      data-testid={`remove-page-${m.key}`}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ThemeStore open={themeStoreOpen} onClose={() => setThemeStoreOpen(false)} />
      <PageStore
        open={pageStoreOpen}
        onClose={() => setPageStoreOpen(false)}
        enabled={enabledPages}
        onAdd={(key) => {
          onAddPage(key);
          // Stay open so the teacher can add multiple in a row, unless
          // they just added the last one.
          if (enabledPages.length + 1 >= totalAvailable) setPageStoreOpen(false);
        }}
      />
    </div>
  );
}
